/**
 * Quantities people type and read (ADR-0038). People type up to TWO decimals (hundredths, the clinic's
 * ask: "Atropine 0.53"); stock is stored to THREE (Rx/DEA amounts are 3-decimal), so on-hand shows up
 * to three and an untouched form sends back exactly the value it was given. Mirrors the backend's
 * common/quantity.ts.
 */

export const TYPED_DECIMALS = 2;
export const STOCK_DECIMALS = 3;

/** Round to `decimals` places; symmetric for negatives and never -0. */
export function roundQuantity(value: number, decimals: number = STOCK_DECIMALS): number {
  const scale = 10 ** decimals;
  const rounded = (Math.sign(value) * Math.round(Math.abs(value) * scale)) / scale;
  return rounded === 0 ? 0 : rounded;
}

/** For display: up to `maxDecimals` places, trailing zeros trimmed, no grouping (9.2, 9.125, 10). */
export function formatQuantity(value: number | null | undefined, maxDecimals: number = STOCK_DECIMALS): string {
  if (value == null || !Number.isFinite(value)) return '';
  return String(roundQuantity(value, maxDecimals));
}

/**
 * Could `text` become a quantity with at most `maxDecimals` places as the person keeps typing?
 * Accepts the in-between states ("", "2.", ".") so a keystroke is never refused halfway.
 */
export function isQuantityDraft(text: string, maxDecimals: number = TYPED_DECIMALS): boolean {
  const pattern = maxDecimals > 0 ? new RegExp(`^\\d*(\\.\\d{0,${maxDecimals}})?$`) : /^\d*$/;
  return pattern.test(text);
}

/** A typed quantity ("0.53", ".5", "2"), or null while it is not one yet. Never more than `maxDecimals`. */
export function parseQuantity(text: string, maxDecimals: number = TYPED_DECIMALS): number | null {
  const trimmed = text.trim().replace(',', '.');
  if (!/\d/.test(trimmed) || !isQuantityDraft(trimmed, maxDecimals)) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}
